// @ts-nocheck
import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Loader2, Search, DollarSign, CheckCircle2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { getSessionUser } from '@/lib/authSession';

interface Props {
  eventId: string | undefined;
}

interface Row {
  key: string;
  source: "tracked" | "registration";
  id: string;
  team: string;
  division: string | null;
  contactName: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  amountDue: number;
  amountReceived: number;
  dueAt: string | null;
  status: string;
  method: string | null;
  notes: string | null;
}

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg border border-border bg-card p-3">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="text-xl font-bold mt-1">{value}</p>
    </div>
  );
}

const money = (n: number) => `$${Number(n || 0).toFixed(0)}`;
const isPaidTracked = (s: string | null) => s === "paid" || s === "not_required" || s === "waived";
const isPaidReg = (s: string | null) => s === "paid" || s === "refunded" || s === "waived";

export function PayLaterSection({ eventId }: Props) {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [sourceFilter, setSourceFilter] = useState<"all" | "tracked" | "registration">("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [divisionFilter, setDivisionFilter] = useState<string>("all");
  const [marking, setMarking] = useState<Row | null>(null);
  const [draft, setDraft] = useState<any>({});
  const [saving, setSaving] = useState(false);

  const reload = useCallback(async () => {
    if (!eventId) return;
    setLoading(true);
    try {
      const [trackedRes, regRes] = await Promise.all([
        supabase
          .from("event_team_tracking")
          .select(
            "id, team_name, division_label, contact_name, contact_email, contact_phone, expected_amount, amount_received, payment_status, payment_method, payment_received_at, payment_notes, status, tracking_type"
          )
          .eq("event_id", eventId),
        supabase
          .from("event_registrations_v2")
          .select(
            "id, team_id, division, display_name_override, primary_billing_email, coach_phone, payment_method_chosen, payment_status, total_amount_due, total_amount_paid, payment_due_at, payment_promise_notes, payment_received_at, payment_received_method, payment_received_amount, payment_received_reference, teams:team_id(name)"
          )
          .eq("event_id", eventId),
      ]);

      if (trackedRes.error) throw trackedRes.error;
      if (regRes.error) throw regRes.error;

      const tracked: Row[] = (trackedRes.data || [])
        .filter((r: any) => {
          if (r.tracking_type === "comp") return false;
          if (isPaidTracked(r.payment_status)) return false;
          const due = Number(r.expected_amount || 0);
          const got = Number(r.amount_received || 0);
          return got < due || due === 0;
        })
        .map((r: any) => ({
          key: `t-${r.id}`,
          source: "tracked" as const,
          id: r.id,
          team: r.team_name,
          division: r.division_label,
          contactName: r.contact_name,
          contactEmail: r.contact_email,
          contactPhone: r.contact_phone,
          amountDue: Number(r.expected_amount || 0),
          amountReceived: Number(r.amount_received || 0),
          dueAt: null,
          status: r.payment_status || "pending",
          method: r.payment_method,
          notes: r.payment_notes,
        }));

      const regs: Row[] = (regRes.data || [])
        .filter((r: any) => !isPaidReg(r.payment_status))
        .map((r: any) => ({
          key: `r-${r.id}`,
          source: "registration" as const,
          id: r.id,
          team: r.display_name_override || r.teams?.name || "Registration",
          division: r.division,
          contactName: null,
          contactEmail: r.primary_billing_email,
          contactPhone: r.coach_phone,
          amountDue: Number(r.total_amount_due || 0),
          amountReceived: Number(r.payment_received_amount || r.total_amount_paid || 0),
          dueAt: r.payment_due_at,
          status: r.payment_status || "pending",
          method: r.payment_received_method || r.payment_method_chosen,
          notes: r.payment_promise_notes,
        }));

      setRows([...tracked, ...regs]);
    } catch (e: any) {
      toast.error(e?.message || "Failed to load pay-later data");
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    reload();
  }, [reload]);

  const divisions = useMemo(
    () => Array.from(new Set(rows.map((r) => r.division).filter(Boolean))) as string[],
    [rows]
  );
  const statuses = useMemo(
    () => Array.from(new Set(rows.map((r) => r.status).filter(Boolean))),
    [rows]
  );

  const now = Date.now();
  const isPastDue = (r: Row) => !!r.dueAt && new Date(r.dueAt).getTime() < now;

  const filtered = useMemo(() => {
    let list = rows;
    if (sourceFilter !== "all") list = list.filter((r) => r.source === sourceFilter);
    if (statusFilter !== "all") list = list.filter((r) => r.status === statusFilter);
    if (divisionFilter !== "all") list = list.filter((r) => r.division === divisionFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((r) => (r.team || "").toLowerCase().includes(q));
    }
    return [...list].sort((a, b) => {
      const pa = isPastDue(a) ? 0 : 1;
      const pb = isPastDue(b) ? 0 : 1;
      if (pa !== pb) return pa - pb;
      return (a.team || "").localeCompare(b.team || "");
    });
  }, [rows, sourceFilter, statusFilter, divisionFilter, search]);

  const stats = useMemo(() => {
    const outstanding = rows.reduce(
      (sum, r) => sum + Math.max(0, r.amountDue - r.amountReceived),
      0
    );
    return {
      outstanding,
      teams: rows.length,
      pastDue: rows.filter(isPastDue).length,
      collected: rows.reduce((sum, r) => sum + r.amountReceived, 0),
    };
  }, [rows]);

  const openMark = (r: Row) => {
    setMarking(r);
    setDraft({
      amount: Math.max(0, r.amountDue - r.amountReceived) || r.amountDue || 0,
      method: r.method || "cash",
      reference: "",
      notes: r.notes || "",
    });
  };

  const submitMark = async () => {
    if (!marking) return;
    setSaving(true);
    try {
      const nowIso = new Date().toISOString();
      if (marking.source === "tracked") {
        const received = Number(marking.amountReceived || 0) + Number(draft.amount || 0);
        const { error } = await supabase
          .from("event_team_tracking")
          .update({
            amount_received: received,
            payment_status: received >= Number(marking.amountDue || 0) ? "paid" : "partial",
            payment_received_at: nowIso,
            payment_method: draft.method || null,
            payment_notes: draft.notes || null,
          })
          .eq("id", marking.id);
        if (error) throw error;
      } else {
        const { data: userRes } = await getSessionUser();
        const received = Number(marking.amountReceived || 0) + Number(draft.amount || 0);
        const { error } = await supabase
          .from("event_registrations_v2")
          .update({
            payment_received_amount: received,
            payment_status: received >= Number(marking.amountDue || 0) ? "paid" : "partial",
            payment_received_at: nowIso,
            payment_received_method: draft.method || null,
            payment_received_reference: draft.reference || null,
            payment_marked_received_by: userRes?.user?.id ?? null,
          })
          .eq("id", marking.id);
        if (error) throw error;
      }
      toast.success("Payment recorded");
      setMarking(null);
      reload();
    } catch (e: any) {
      toast.error(e?.message || "Failed to record payment");
    } finally {
      setSaving(false);
    }
  };

  if (!eventId) {
    return (
      <div className="p-8 text-center text-muted-foreground">
        Save the event first, then track outstanding payments here.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold tracking-tight">PAY LATER</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Everyone who still owes money for this event — tracked teams paying at the door and
          public registrations that chose to pay later.
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Total Outstanding" value={money(stats.outstanding)} />
        <StatCard label="Teams Awaiting Payment" value={stats.teams} />
        <StatCard label="Past Due" value={stats.pastDue} />
        <StatCard label="Collected" value={money(stats.collected)} />
      </div>

      <div className="flex flex-wrap items-end gap-3 p-3 rounded-lg bg-muted/40 border border-border">
        <div className="flex-1 min-w-[200px]">
          <div className="relative">
            <Search className="absolute left-2 top-2.5 w-4 h-4 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search team name…"
              className="pl-8 h-9"
            />
          </div>
        </div>
        <select
          value={sourceFilter}
          onChange={(e) => setSourceFilter(e.target.value as any)}
          className="h-9 rounded-md border border-input bg-background px-2 text-sm"
        >
          <option value="all">All sources</option>
          <option value="tracked">Tracked</option>
          <option value="registration">Registration</option>
        </select>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="h-9 rounded-md border border-input bg-background px-2 text-sm"
        >
          <option value="all">All statuses</option>
          {statuses.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <select
          value={divisionFilter}
          onChange={(e) => setDivisionFilter(e.target.value)}
          className="h-9 rounded-md border border-input bg-background px-2 text-sm"
        >
          <option value="all">All divisions</option>
          {divisions.map((d) => (
            <option key={d} value={d}>
              {d}
            </option>
          ))}
        </select>
      </div>

      <div className="border border-border rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-8 flex justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center">
            <DollarSign className="w-10 h-10 mx-auto text-muted-foreground mb-2" />
            <p className="text-sm text-muted-foreground">
              Nothing outstanding. Pay-at-door teams and pay-later registrations will appear here
              until their balance is collected.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="text-left px-3 py-2">Source</th>
                  <th className="text-left px-3 py-2">Team</th>
                  <th className="text-left px-3 py-2">Division</th>
                  <th className="text-left px-3 py-2">Contact</th>
                  <th className="text-left px-3 py-2">Amount Due</th>
                  <th className="text-left px-3 py-2">Amount Received</th>
                  <th className="text-left px-3 py-2">Due Date</th>
                  <th className="text-left px-3 py-2">Status</th>
                  <th className="text-right px-3 py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.key} className="border-t border-border">
                    <td className="px-3 py-2">
                      <span
                        className={cn(
                          "inline-flex px-2 py-0.5 rounded-full text-xs font-medium",
                          r.source === "tracked"
                            ? "bg-amber-100 text-amber-800"
                            : "bg-blue-100 text-blue-800"
                        )}
                      >
                        {r.source === "tracked" ? "Tracked Team" : "Registration"}
                      </span>
                    </td>
                    <td className="px-3 py-2 font-medium">{r.team}</td>
                    <td className="px-3 py-2">{r.division || "—"}</td>
                    <td className="px-3 py-2 text-xs">
                      {r.contactName && <div>{r.contactName}</div>}
                      {r.contactEmail && (
                        <div className="text-muted-foreground">{r.contactEmail}</div>
                      )}
                      {r.contactPhone && (
                        <div className="text-muted-foreground">{r.contactPhone}</div>
                      )}
                      {!r.contactName && !r.contactEmail && !r.contactPhone && "—"}
                    </td>
                    <td className="px-3 py-2">{money(r.amountDue)}</td>
                    <td className="px-3 py-2">{money(r.amountReceived)}</td>
                    <td className="px-3 py-2">
                      {r.dueAt ? (
                        <span className={cn(isPastDue(r) && "text-red-600 font-medium")}>
                          {new Date(r.dueAt).toLocaleDateString()}
                        </span>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-muted text-muted-foreground">
                        {r.status}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button
                        onClick={() => openMark(r)}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-border text-xs font-medium hover:bg-secondary"
                      >
                        <CheckCircle2 className="w-3.5 h-3.5" /> Mark Received
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={!!marking} onOpenChange={(o) => !o && setMarking(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Mark payment received</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Amount received</Label>
              <Input
                type="number"
                value={draft.amount ?? 0}
                onChange={(e) => setDraft((d: any) => ({ ...d, amount: e.target.value }))}
              />
            </div>
            <div>
              <Label>Method</Label>
              <select
                value={draft.method || "cash"}
                onChange={(e) => setDraft((d: any) => ({ ...d, method: e.target.value }))}
                className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm"
              >
                <option value="cash">Cash</option>
                <option value="check">Check</option>
                <option value="venmo">Venmo</option>
                <option value="card">Card</option>
                <option value="other">Other</option>
              </select>
            </div>
            {marking?.source === "registration" ? (
              <div>
                <Label>Reference</Label>
                <Input
                  value={draft.reference || ""}
                  onChange={(e) => setDraft((d: any) => ({ ...d, reference: e.target.value }))}
                  placeholder="Check #, confirmation code…"
                />
              </div>
            ) : (
              <div>
                <Label>Notes</Label>
                <Input
                  value={draft.notes || ""}
                  onChange={(e) => setDraft((d: any) => ({ ...d, notes: e.target.value }))}
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <button
              onClick={() => setMarking(null)}
              className="px-3 py-2 rounded-md border border-border text-sm"
            >
              Cancel
            </button>
            <button
              onClick={submitMark}
              disabled={saving}
              className="px-3 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium disabled:opacity-60"
            >
              {saving ? "Saving…" : "Save"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default PayLaterSection;
